import type { ParseKeys } from 'i18next';
import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { Calendar } from '@/components/Icons/Calendar';
import { CalendarDeadline } from '@/components/Icons/CalendarDeadline';
import { Cancel } from '@/components/Icons/Cancel';
import { CheckCircleFilled } from '@/components/Icons/CheckCircleFilled';
import { CheckCircleSolid } from '@/components/Icons/CheckCircleSolid';
import { CircleClock } from '@/components/Icons/CircleClock';
import { ClockFilled } from '@/components/Icons/ClockFilled';
import { Group } from '@/components/Icons/Group';
import { UserSingle } from '@/components/Icons/UserSingle';
import { WebcamVideo } from '@/components/Icons/WebcamVideo';
import { Skeleton } from '@/components/Skeleton';
import { useVideoCardActions } from '@/pages/Mail/hooks/useVideoCardActions';
import {
  BorderRadius,
  ButtonVariant,
  Size,
  VideoMeetingDisplayStatus,
  VideoMeetingType,
} from '@/types/components';
import type { VideoMeeting } from '@/types/video';
import { stopPropagation } from '@/utils/global';
import { sanitizeRichTextHtml } from '@/utils/html';
import { tv } from '@/utils/tv';

const buttonIcon = tv({
  base: 'shrink-0',
  variants: {
    kind: { respond: '', join: '' },
    mobile: { true: '', false: 'size-4' },
  },
  compoundVariants: [
    { kind: 'respond', mobile: true, class: 'size-5' },
    { kind: 'join', mobile: true, class: 'size-5.5' },
  ],
  defaultVariants: { kind: 'respond' },
});

const meetingIcon = <WebcamVideo className="size-4 shrink-0 text-primary" />;

const typeChip: Record<
  VideoMeetingType,
  { labelKey: ParseKeys; icon: ReactNode }
> = {
  [VideoMeetingType.Meeting]: {
    labelKey: 'mail.video.type.meeting',
    icon: meetingIcon,
  },
  [VideoMeetingType.ParentTeacher]: {
    labelKey: 'mail.video.type.parentTeacher',
    icon: meetingIcon,
  },
  [VideoMeetingType.Lesson]: {
    labelKey: 'mail.video.type.lesson',
    icon: meetingIcon,
  },
  [VideoMeetingType.Individual]: {
    labelKey: 'mail.video.type.individual',
    icon: <Group className="size-4 shrink-0 text-primary" />,
  },
  [VideoMeetingType.Other]: {
    labelKey: 'mail.video.type.other',
    icon: meetingIcon,
  },
};

const statusChip: Record<
  VideoMeetingDisplayStatus,
  { labelKey: ParseKeys; className: string; icon: ReactNode }
> = {
  [VideoMeetingDisplayStatus.Confirmed]: {
    labelKey: 'mail.video.status.confirmed',
    className: 'bg-success-surface',
    icon: <CheckCircleFilled className="size-4 shrink-0" />,
  },
  [VideoMeetingDisplayStatus.Pending]: {
    labelKey: 'mail.video.status.pending',
    className: 'bg-warning-surface',
    icon: <ClockFilled className="size-4 shrink-0 text-warning" />,
  },
  [VideoMeetingDisplayStatus.Declined]: {
    labelKey: 'mail.video.status.declined',
    className: 'bg-red-soft',
    icon: <Cancel className="size-4 shrink-0 text-destructive" />,
  },
  [VideoMeetingDisplayStatus.Canceled]: {
    labelKey: 'mail.video.status.canceled',
    className: 'bg-red-soft',
    icon: <Cancel className="size-4 shrink-0 text-destructive" />,
  },
  [VideoMeetingDisplayStatus.Ended]: {
    labelKey: 'mail.video.status.ended',
    className: 'bg-on-primary-surface',
    icon: (
      <CalendarDeadline className="size-4 shrink-0 text-subtle-foreground" />
    ),
  },
};

interface Props {
  meeting: VideoMeeting;
  isMobile: boolean;
  isSummary?: boolean;
  isResponding?: boolean;
  onOpen?: (id: string) => void;
  onJoin?: (id: string) => void;
  onApprove?: (id: string) => void;
  onDecline?: (id: string) => void;
}

export const VideoCard: React.FC<Props> = ({
  meeting,
  isMobile,
  isSummary,
  isResponding,
  onOpen,
  onJoin,
  onApprove,
  onDecline,
}) => {
  const { t } = useTranslation();

  const {
    isJoinVisible,
    isDeclineVisible,
    isApproveVisible,
    hasActions,
    isJoinEnabled,
    isClickable,
  } = useVideoCardActions({ meeting, isSummary });

  const {
    type,
    displayStatus,
    title,
    htmlBody,
    authorName,
    authorSubject,
    date,
    time,
    participantCount,
  } = meeting;
  const typeConfig = type ? typeChip[type] : null;
  const statusConfig = displayStatus ? statusChip[displayStatus] : null;
  const hasChips = !!typeConfig || !!statusConfig;
  const titleClass = hasActions ? 'text-callout-16' : 'text-body-16';
  const authorNameClass = isMobile ? 'text-caption-13' : 'text-body-14';
  const authorRoleClass = isMobile ? 'text-body-13' : 'text-body-14';

  const buttonSize = isMobile ? Size.Large : Size.Small;
  const buttonRadius = isMobile ? BorderRadius.Pill : undefined;

  const chipRow = hasChips && (
    <div className="flex items-center gap-2">
      {typeConfig && (
        <span className="inline-flex items-center gap-1 rounded-xl bg-purple-soft px-2 py-0.5">
          {typeConfig.icon}
          <span className="text-caption-12 whitespace-nowrap text-foreground">
            {t(typeConfig.labelKey)}
          </span>
        </span>
      )}
      {statusConfig && (
        <span
          className={`inline-flex items-center gap-1 rounded-xl py-0.5 pr-2 pl-1 ${statusConfig.className}`}
        >
          {statusConfig.icon}
          <span className="text-caption-12 whitespace-nowrap text-foreground">
            {t(statusConfig.labelKey)}
          </span>
        </span>
      )}
    </div>
  );

  const body = (
    <div className="flex min-w-0 flex-col gap-1">
      {isSummary && htmlBody && (
        <div
          className="rich-text text-body-16 text-foreground"
          dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(htmlBody) }}
        />
      )}
      {!isSummary && title && (
        <p className={`truncate text-foreground ${titleClass}`}>{title}</p>
      )}
      <p className="flex min-w-0 gap-1">
        <span className={`truncate text-muted-foreground ${authorNameClass}`}>
          {authorName}
        </span>
        <span className={`shrink-0 text-subtle-foreground ${authorRoleClass}`}>
          ({authorSubject})
        </span>
      </p>
    </div>
  );

  const metaRow = (
    <div className="text-caption-12 flex flex-wrap items-center gap-x-3 gap-y-1 text-subtle-foreground">
      <span className="flex items-center gap-1">
        <Calendar className="size-4 shrink-0" />
        {date}
      </span>
      <span className="flex items-center gap-1">
        <CircleClock className="size-4 shrink-0" />
        {time}
      </span>
      {!!participantCount && (
        <span className="flex items-center gap-1">
          <UserSingle className="size-4 shrink-0" />
          {participantCount}
        </span>
      )}
    </div>
  );

  const actions = hasActions && (
    <div
      onClick={stopPropagation}
      className="flex shrink-0 gap-3 max-md:w-full md:flex-col"
    >
      {isDeclineVisible && (
        <Button
          variant={ButtonVariant.Secondary}
          size={buttonSize}
          radius={buttonRadius}
          className="text-destructive max-md:flex-1"
          onClick={() => onDecline?.(meeting.id)}
        >
          <Cancel className={buttonIcon({ mobile: isMobile })} />
          {t('mail.video.decline')}
        </Button>
      )}
      {isApproveVisible && (
        <Button
          variant={ButtonVariant.Primary}
          size={buttonSize}
          radius={buttonRadius}
          className="max-md:flex-1"
          onClick={() => onApprove?.(meeting.id)}
        >
          <CheckCircleSolid className={buttonIcon({ mobile: isMobile })} />
          {t('mail.video.approve')}
        </Button>
      )}
      {isJoinVisible && (
        <Button
          variant={ButtonVariant.Primary}
          size={buttonSize}
          radius={buttonRadius}
          disabled={!isJoinEnabled}
          className="max-md:w-full"
          onClick={() => onJoin?.(meeting.id)}
        >
          <WebcamVideo
            className={buttonIcon({ kind: 'join', mobile: isMobile })}
          />
          {t('mail.video.join')}
        </Button>
      )}
    </div>
  );

  const actionSkeleton = hasActions && (
    <div className="flex shrink-0 gap-3 max-md:w-full md:flex-col">
      {isDeclineVisible && (
        <Skeleton className="h-8 w-[118px] max-md:h-12 max-md:flex-1" />
      )}
      {isApproveVisible && (
        <Skeleton className="h-8 w-[118px] max-md:h-12 max-md:flex-1" />
      )}
      {isJoinVisible && (
        <Skeleton className="h-8 w-[118px] max-md:h-12 max-md:w-full" />
      )}
    </div>
  );

  const chips =
    isResponding && chipRow ? <Skeleton className="h-[22px] w-60" /> : chipRow;
  const actionRow = isResponding ? actionSkeleton : actions;

  if (isMobile) {
    return (
      <div
        onClick={() => onOpen?.(meeting.id)}
        className={`flex flex-col gap-5 rounded-xl bg-card p-4 shadow-card ${isClickable ? 'cursor-pointer transition-colors hover:bg-hover' : ''}`}
      >
        <div className="flex flex-col gap-4">
          {chips}
          <div className="flex flex-col gap-3">
            {body}
            {metaRow}
          </div>
        </div>
        {actionRow}
      </div>
    );
  }

  return (
    <div
      onClick={() => onOpen?.(meeting.id)}
      className={`flex items-center gap-6 rounded-xl bg-card p-4 shadow-card ${isClickable ? 'cursor-pointer transition-colors hover:bg-hover' : ''}`}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {chips}
        {body}
        {metaRow}
      </div>
      {actionRow}
    </div>
  );
};
